import React, { useEffect, useState, useContext } from 'react';
import api from '../config/axiosConfig';
import UserGroupContext from '../contexts/UserGroupContext';
import { exportDashBoardDataToExcel } from '../utils/exportUtils';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Info,
  LocalAtmOutlined,
  MonetizationOn,
  PaymentsOutlined,
  PriceChange,
  RequestQuote,
  SaveAlt
} from '@mui/icons-material';

import styles from './css/DashBoard.module.css';

// Tabs
import AdminTab from './DashBoardTabs/AdminTab';
import AccountingTab from './DashBoardTabs/AccountingTab';
import YearlytargetTab from './DashBoardTabs/YearlytargetTab';

function DashBoard() {
  const userGroups = useContext(UserGroupContext);

  // 一開始先設為 null，代表尚未決定線別
  const [country, setCountry] = useState(null);

  // 控制年份 / 月份
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  // 只有管理員 / 會計人員需要的篩選
  const [filterSalesRep, setFilterSalesRep] = useState(null);
  const [filterCreatedDate, setFilterCreatedDate] = useState('');
  const [salesRepList, setSalesRepList] = useState([]);

  // 給管理員 / 會計人員使用：當月 & 上月資料
  const [currentMonthData, setCurrentMonthData] = useState([]);
  const [lastMonthData, setLastMonthData] = useState([]);

  // 給「日本線管理」使用
  const [currentMonthDataJPStaff, setCurrentMonthDataJPStaff] = useState({});
  const [lastMonthDataJPStaff, setLastMonthDataJPStaff] = useState({});

  // 給「韓國線管理」使用
  const [currentMonthDataKRStaff, setCurrentMonthDataKRStaff] = useState({});
  const [lastMonthDataKRStaff, setLastMonthDataKRStaff] = useState({});

  // Tab (0: 收益, 1: 款項)
  const [tabValue, setTabValue] = useState(0);

  // 下拉選單可選年份
  const years = Array.from({ length: new Date().getFullYear() - 2022 + 1 }, (_, i) => 2023 + i);

  // ========== 1) 只在 country===null 時決定線別，執行一次後就不再改 ==========
  useEffect(() => {
    if (country !== null) return; 
    if (userGroups.includes('日本線管理')) {
      setCountry('JP');
    } else if (userGroups.includes('韓國線管理')) {
      setCountry('KR');
    } else {
      // 管理員 / 會計人員（或沒有任何線管理身分）=> 預設給 'JP' (可自行改 'KR')
      setCountry('JP');
    }
  }, [userGroups, country]);

  // 管理員 / 會計人員載入業務列表（只需執行一次）
  useEffect(() => {
    if (userGroups.includes('管理員') || userGroups.includes('會計人員')) {
      const fetchSalesRepList = async () => {
        try {
          const response = await api.get('/core/api/user_basic_info/');
          setSalesRepList(response.data);
        } catch (error) {
          console.error(error);
        }
      };
      fetchSalesRepList();
    }
  }, [userGroups]);

  // ========== 2) 管理員 / 會計人員：抓對應線別 (country) 的資料 ==========
  useEffect(() => {
    if (!country) return; // 尚未決定線別
    if (userGroups.includes('管理員') || userGroups.includes('會計人員')) {
      const fetchAdminData = async () => {
        try {
          const lineApiPrefix = country === 'KR' ? '/korea/api' : '/japan/api';
          const dataResponse = await api.get(
            `${lineApiPrefix}/dashboard/?type=order_data_by_month_for_admin_detail&year=${year}&month=${month}`
          );
          setCurrentMonthData(dataResponse.data.current_month);
          setLastMonthData(dataResponse.data.last_month);
        } catch (error) {
          console.error(error);
        }
      };
      fetchAdminData();
    }
  }, [country, year, month, userGroups]);

  // ========== 3) 日本線管理：抓日本線專用數據 ==========
  useEffect(() => {
    if (!country || country !== 'JP') return; 
    if (userGroups.includes('日本線管理')) {
      const fetchJPData = async () => {
        try {
          const dataResponse = await api.get(
            `/japan/api/dashboard/?type=order_data_by_month_for_japan_staff&year=${year}&month=${month}`
          );
          setCurrentMonthDataJPStaff(dataResponse.data.current_month);
          setLastMonthDataJPStaff(dataResponse.data.last_month);
        } catch (error) {
          console.error(error);
        }
      };
      fetchJPData();
    }
  }, [country, year, month, userGroups]);

  // ========== 4) 韓國線管理：抓韓國線專用數據 ==========
  useEffect(() => {
    if (!country || country !== 'KR') return;
    if (userGroups.includes('韓國線管理')) {
      const fetchKRData = async () => {
        try {
          const dataResponse = await api.get(
            `/korea/api/dashboard/?type=order_data_by_month_for_korea_staff&year=${year}&month=${month}`
          );
          setCurrentMonthDataKRStaff(dataResponse.data.current_month);
          setLastMonthDataKRStaff(dataResponse.data.last_month);
        } catch (error) {
          console.error(error);
        }
      };
      fetchKRData();
    }
  }, [country, year, month, userGroups]);

  // Tab 切換時，重置一些篩選
  const handleTabChange = (newValue) => {
    setTabValue(newValue);
    setFilterSalesRep(null);
    setFilterCreatedDate('');
  };

  // AdminTab 回傳更新後的資料
  const handleOrderDataRecieved = (currentData, lastData) => {
    setCurrentMonthData(currentData);
    setLastMonthData(lastData);
  };

  // AdminTab / AccountingTab 切換
  const renderTabContent = () => {
    switch (tabValue) {
      case 0:
        return (
          <AdminTab
            country={country}
            onOrderDataSend={handleOrderDataRecieved}
            year={year}
            month={month}
            filterSalesRep={filterSalesRep}
            filterCreatedDate={filterCreatedDate}
          />
        );
      case 1:
        return <AccountingTab country={country} year={year} month={month} />;
      default:
        return null;
    }
  };

  // 動態顯示「韓幣 (₩)」或「日幣 (￥)」
  const currencySymbol = country === 'KR' ? '₩' : '￥';

  // 若 country === null，代表尚未決定線別 => 可顯示個 Loading UI
  if (country === null) {
    return (
      <Container maxWidth={false}>
        <Box sx={{ textAlign: 'center', mt: 5 }}>
          <Typography>載入中，請稍候…</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false}>
      {/* 
        ============= 頂部國家切換 (僅給管理員 / 會計人員) =============
      */}
      {(userGroups.includes('管理員') || userGroups.includes('會計人員')) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            mb: 2,
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
            gap: 2
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            國家線別：
          </Typography>
          <Autocomplete
            options={['JP', 'KR']}
            getOptionLabel={(option) => (option === 'KR' ? '韓國線' : '日本線')}
            disableClearable
            value={country}
            sx={{ width: 130 }}
            onChange={(event, newValue) => {
              setCountry(newValue);
              setFilterSalesRep(null);
              setFilterCreatedDate('');
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="" variant="standard" />
            )}
          />
        </Box>
      )}

      {/* 年度目標 */}
      <YearlytargetTab country={country} />

      <Box className={styles['dataCard-box']}>
        {/* 
          ========== 原本的標題區域：保留 [年份 / 月份] 下拉 ==========
        */}
        <Box className={styles['dataCard-title-box']}>
          <Typography sx={{ fontSize: '20px', fontWeight: 'bold', color: '#1a224c' }}>
            當前數據源
            <br />
            <span style={{ fontSize: '15px', color: 'GrayText', paddingLeft: '14px' }}>
              {year}年{month}月
            </span>
            {filterSalesRep && (
              <span style={{ fontSize: '15px', color: 'GrayText', paddingLeft: '14px' }}>
                業務：
                {salesRepList.find((user) => user.id === filterSalesRep)?.profile?.nickname ||
                  salesRepList.find((user) => user.id === filterSalesRep)?.username}
              </span>
            )}
          </Typography>

          {/* 右側保留 年 / 月 下拉 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <Autocomplete
              options={years}
              getOptionLabel={(option) => String(option)}
              disableClearable
              value={year}
              sx={{ width: '75px' }}
              renderInput={(params) => (
                <TextField {...params} size="small" label="年份選擇" variant="standard" />
              )}
              onChange={(event, newValue) => {
                setYear(newValue || new Date().getFullYear());
                setFilterSalesRep(null);
                setFilterCreatedDate('');
              }}
            />
            <Autocomplete
              options={Array.from({ length: 12 }, (_, i) => i + 1)}
              getOptionLabel={(option) => String(option)}
              disableClearable
              value={month}
              sx={{ width: '55px' }}
              renderInput={(params) => (
                <TextField {...params} size="small" label="月份選擇" variant="standard" />
              )}
              onChange={(event, newValue) => {
                setMonth(newValue || new Date().getMonth() + 1);
                setFilterSalesRep(null);
                setFilterCreatedDate('');
              }}
            />
          </Box>
        </Box>

        {/* 卡片顯示區（收益概況），依角色 & country 顯示 */}
        <Box sx={{ display: 'flex', marginBottom: '10px' }}>
          <Box className={styles['dataCard-details-box']}>
            {/* (1) 管理員 / 會計人員 */}
            {(userGroups.includes('管理員') || userGroups.includes('會計人員')) && (
              <>
                {/* 外幣報價 */}
                <Card elevation={3} className={styles['details-card']}>
                  <CardContent className={styles['details-cardcontent']}>
                    <LocalAtmOutlined
                      className={styles['details-icon']}
                      sx={{ backgroundColor: '#fa5a7d' }}
                    />
                    <Typography className={styles['details-number']}>
                      {currencySymbol}
                      {currentMonthData
                        .filter((order) =>
                          filterSalesRep ? order.sales_rep === filterSalesRep : true
                        )
                        .reduce((acc, order) => acc + Number(order.orderfortable__fc_price), 0) || '0'}
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold', color: '#7a7b8c' }}>
                      外幣報價總和
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#9cabe9' }}>
                      {(() => {
                        const currentMonthPrice =
                          currentMonthData
                            .filter((order) =>
                              filterSalesRep ? order.sales_rep === filterSalesRep : true
                            )
                            .reduce(
                              (acc, order) => acc + Number(order.orderfortable__fc_price),
                              0
                            ) || 0;
                        const lastMonthPrice =
                          lastMonthData
                            .filter((order) =>
                              filterSalesRep ? order.sales_rep === filterSalesRep : true
                            )
                            .reduce(
                              (acc, order) => acc + Number(order.orderfortable__fc_price),
                              0
                            ) || 0;
                        if (lastMonthPrice === 0) return '上月無數據，無法對比';

                        const diffPct =
                          ((currentMonthPrice - lastMonthPrice) / lastMonthPrice) * 100;
                        return (
                          <span>
                            較上月{' '}
                            <span style={{ color: diffPct >= 0 ? '#fc1a01' : '#27b044' }}>
                              {diffPct >= 0 ? '+ ' : '- '}
                              {Math.abs(diffPct).toFixed(2)}%
                            </span>
                          </span>
                        );
                      })()}
                    </Typography>
                    <Typography className={styles['details-description']}>
                      註: 外幣報價總和為所有訂單的外幣報價總和，包含所有狀態之訂單。
                    </Typography>
                  </CardContent>
                </Card>

                {/* 預估外幣報價 */}
                <Card elevation={3} className={styles['details-card']}>
                  <CardContent className={styles['details-cardcontent']}>
                    <PriceChange
                      className={styles['details-icon']}
                      sx={{ backgroundColor: '#749cf0' }}
                    />
                    <Typography className={styles['details-number']}>
                      {currencySymbol}
                      {currentMonthData
                        .filter((order) =>
                          filterSalesRep ? order.sales_rep === filterSalesRep : true
                        )
                        .reduce(
                          (acc, order) => acc + Number(order.orderfortable__fc_price_estimated),
                          0
                        ) || '0'}
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold', color: '#7a7b8c' }}>
                      預估外幣報價總和
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#9cabe9' }}>
                      {(() => {
                        const actualSum =
                          currentMonthData
                            .filter((order) =>
                              filterSalesRep ? order.sales_rep === filterSalesRep : true
                            )
                            .reduce(
                              (acc, order) => acc + Number(order.orderfortable__fc_price),
                              0
                            ) || 0;
                        const estimatedSum =
                          currentMonthData
                            .filter((order) =>
                              filterSalesRep ? order.sales_rep === filterSalesRep : true
                            )
                            .reduce(
                              (acc, order) =>
                                acc + Number(order.orderfortable__fc_price_estimated),
                              0
                            ) || 0;
                        if (estimatedSum === 0) return '無法計算實際報價';

                        const diffPct = ((actualSum - estimatedSum) / estimatedSum) * 100;
                        return (
                          <span>
                            實際報價{' '}
                            <span style={{ color: diffPct >= 0 ? '#fc1a01' : '#27b044' }}>
                              {diffPct >= 0 ? '+ ' : '- '}
                              {Math.abs(diffPct).toFixed(2)}%
                            </span>
                          </span>
                        );
                      })()}
                    </Typography>
                    <Typography className={styles['details-description']}>
                      註: 實際報價 = (外幣報價總和 - 預估外幣報價總和) / 預估外幣報價總和
                    </Typography>
                  </CardContent>
                </Card>

                {/* 外站報價 */}
                <Card elevation={3} className={styles['details-card']}>
                  <CardContent className={styles['details-cardcontent']}>
                    <RequestQuote
                      className={styles['details-icon']}
                      sx={{ backgroundColor: '#96d174' }}
                    />
                    <Typography className={styles['details-number']}>
                      {currencySymbol}
                      {currentMonthData
                        .filter((order) =>
                          filterSalesRep ? order.sales_rep === filterSalesRep : true
                        )
                        .reduce(
                          (acc, order) => acc + Number(order.charterbooking__booking_price),
                          0
                        ) || '0'}
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold', color: '#7a7b8c' }}>
                      外站報價總和
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#9cabe9' }}>
                      {(() => {
                        const currentMonthPrice =
                          currentMonthData
                            .filter((order) =>
                              filterSalesRep ? order.sales_rep === filterSalesRep : true
                            )
                            .reduce(
                              (acc, order) => acc + Number(order.charterbooking__booking_price),
                              0
                            ) || 0;
                        const lastMonthPrice =
                          lastMonthData
                            .filter((order) =>
                              filterSalesRep ? order.sales_rep === filterSalesRep : true
                            )
                            .reduce(
                              (acc, order) => acc + Number(order.charterbooking__booking_price),
                              0
                            ) || 0;
                        if (lastMonthPrice === 0) return '上月無數據，無法對比';

                        const diffPct =
                          ((currentMonthPrice - lastMonthPrice) / lastMonthPrice) * 100;
                        return (
                          <span>
                            較上月{' '}
                            <span style={{ color: diffPct >= 0 ? '#fc1a01' : '#27b044' }}>
                              {diffPct >= 0 ? '+ ' : '- '}
                              {Math.abs(diffPct).toFixed(2)}%
                            </span>
                          </span>
                        );
                      })()}
                    </Typography>
                    <Typography className={styles['details-description']}>
                      註: 外站報價總和為所有訂單的外站報價總和，包含所有狀態之訂單。
                    </Typography>
                  </CardContent>
                </Card>

                {/* 獲利總和 */}
                <Card elevation={3} className={styles['details-card']}>
                  <CardContent className={styles['details-cardcontent']}>
                    <MonetizationOn
                      className={styles['details-icon']}
                      sx={{ backgroundColor: '#fe947a' }}
                    />
                    <Typography className={styles['details-number']}>
                      {currencySymbol}
                      {currentMonthData
                        .filter((order) =>
                          filterSalesRep ? order.sales_rep === filterSalesRep : true
                        )
                        .reduce(
                          (acc, order) =>
                            acc +
                            Number(
                              order.orderfortable__fc_price -
                                order.charterbooking__booking_price -
                                order.total_advance_amount
                            ),
                          0
                        ) || '0'}
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold', color: '#7a7b8c' }}>
                      獲利總和
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#9cabe9' }}>
                      {(() => {
                        const currentMonthPrice =
                          currentMonthData
                            .filter((order) =>
                              filterSalesRep ? order.sales_rep === filterSalesRep : true
                            )
                            .reduce(
                              (acc, order) =>
                                acc +
                                Number(
                                  order.orderfortable__fc_price -
                                    order.charterbooking__booking_price -
                                    order.total_advance_amount
                                ),
                              0
                            ) || 0;
                        const lastMonthPrice =
                          lastMonthData
                            .filter((order) =>
                              filterSalesRep ? order.sales_rep === filterSalesRep : true
                            )
                            .reduce(
                              (acc, order) =>
                                acc +
                                Number(
                                  order.orderfortable__fc_price -
                                    order.charterbooking__booking_price -
                                    order.total_advance_amount
                                ),
                              0
                            ) || 0;
                        if (lastMonthPrice === 0) return '上月無數據，無法對比';
                        if (lastMonthPrice < 0 && currentMonthPrice >= 0) {
                          return <span style={{ color: '#27b044' }}>本月由虧轉盈</span>;
                        }

                        const diffPct =
                          ((currentMonthPrice - lastMonthPrice) / lastMonthPrice) * 100;
                        return (
                          <span>
                            較上月{' '}
                            <span style={{ color: diffPct >= 0 ? '#fc1a01' : '#27b044' }}>
                              {diffPct >= 0 ? '+ ' : '- '}
                              {Math.abs(diffPct).toFixed(2)}%
                            </span>
                          </span>
                        );
                      })()}
                    </Typography>
                    <Typography className={styles['details-description']}>
                      註: 獲利總和 = 外幣報價總和 - (外站報價總和 + 代墊(退)款總和)
                    </Typography>
                  </CardContent>
                </Card>

                {/* 外站代收總和 */}
                <Card elevation={3} className={styles['details-card']}>
                  <CardContent className={styles['details-cardcontent']}>
                    <PaymentsOutlined
                      className={styles['details-icon']}
                      sx={{ backgroundColor: '#bf83ff' }}
                    />
                    <Typography className={styles['details-number']}>
                      {currencySymbol}
                      {currentMonthData
                        .filter((order) =>
                          filterSalesRep ? order.sales_rep === filterSalesRep : true
                        )
                        .reduce(
                          (acc, order) => acc + Number(order.orderfortable__jp_recive_price),
                          0
                        ) || '0'}
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold', color: '#7a7b8c' }}>
                      外站代收總和
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#9cabe9' }}>
                      {(() => {
                        const profit =
                          currentMonthData
                            .filter((order) =>
                              filterSalesRep ? order.sales_rep === filterSalesRep : true
                            )
                            .reduce(
                              (acc, order) =>
                                acc +
                                Number(
                                  order.orderfortable__fc_price -
                                    order.charterbooking__booking_price -
                                    order.total_advance_amount
                                ),
                              0
                            ) || 0;
                        const recive =
                          currentMonthData
                            .filter((order) =>
                              filterSalesRep ? order.sales_rep === filterSalesRep : true
                            )
                            .reduce(
                              (acc, order) =>
                                acc + Number(order.orderfortable__jp_recive_price),
                              0
                            ) || 0;
                        if (profit === 0) return '無法計算代收佔獲利比';

                        const percentage = (recive / profit) * 100;
                        return (
                          <span>
                            外站代收佔比{' '}
                            <span style={{ color: '#fc1a01' }}>{percentage.toFixed(2)}%</span>
                          </span>
                        );
                      })()}
                    </Typography>
                    <Typography className={styles['details-description']}>
                      註: 外站代收占比 = 外站代收總和 / 獲利總和
                    </Typography>
                  </CardContent>
                </Card>
              </>
            )}

            {/* (2) 日本線管理 → 只顯示日本線數據 */}
            {userGroups.includes('日本線管理') && (
              <>
                <Card elevation={3} className={styles['details-card']}>
                  <CardContent className={styles['details-cardcontent']}>
                    <LocalAtmOutlined
                      className={styles['details-icon']}
                      sx={{ backgroundColor: '#fa5a7d' }}
                    />
                    <Typography className={styles['details-number']}>
                      ￥ {currentMonthDataJPStaff?.fc_price_sum || '0'}
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold', color: '#7a7b8c' }}>
                      外幣報價總和
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#9cabe9' }}>
                      {(() => {
                        const lastSum = lastMonthDataJPStaff?.fc_price_sum || 0;
                        if (lastSum === 0) return '上月無數據，無法對比';
                        const diffPct =
                          ((currentMonthDataJPStaff.fc_price_sum - lastSum) / lastSum) * 100;
                        return (
                          <span>
                            較上月{' '}
                            <span style={{ color: diffPct >= 0 ? '#fc1a01' : '#27b044' }}>
                              {diffPct >= 0 ? '+ ' : '- '}
                              {Math.abs(diffPct).toFixed(2)}%
                            </span>
                          </span>
                        );
                      })()}
                    </Typography>
                  </CardContent>
                </Card>
                <Card elevation={3} className={styles['details-card']}>
                  <CardContent className={styles['details-cardcontent']}>
                    <PriceChange
                      className={styles['details-icon']}
                      sx={{ backgroundColor: '#749cf0' }}
                    />
                    <Typography className={styles['details-number']}>
                      ￥ {currentMonthDataJPStaff?.fc_price_estimated_sum || '0'}
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold', color: '#7a7b8c' }}>
                      預估外幣報價總和
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#9cabe9' }}>
                      {(() => {
                        const actual = currentMonthDataJPStaff.fc_price_sum || 0;
                        const estimated = currentMonthDataJPStaff.fc_price_estimated_sum || 0;
                        if (estimated === 0) return '無法計算實際報價';
                        const diffPct = ((actual - estimated) / estimated) * 100;
                        return (
                          <span>
                            實際報價{' '}
                            <span style={{ color: diffPct >= 0 ? '#fc1a01' : '#27b044' }}>
                              {diffPct >= 0 ? '+ ' : '- '}
                              {Math.abs(diffPct).toFixed(2)}%
                            </span>
                          </span>
                        );
                      })()}
                    </Typography>
                  </CardContent>
                </Card>
                <Card elevation={3} className={styles['details-card']}>
                  <CardContent className={styles['details-cardcontent']}>
                    <RequestQuote
                      className={styles['details-icon']}
                      sx={{ backgroundColor: '#96d174' }}
                    />
                    <Typography className={styles['details-number']}>
                      ￥ {currentMonthDataJPStaff?.booking_price_sum || '0'}
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold', color: '#7a7b8c' }}>
                      外站報價總和
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#9cabe9' }}>
                      {(() => {
                        const lastSum = lastMonthDataJPStaff?.booking_price_sum || 0;
                        if (lastSum === 0) return '上月無數據，無法對比';
                        const diffPct =
                          ((currentMonthDataJPStaff.booking_price_sum - lastSum) / lastSum) * 100;
                        return (
                          <span>
                            較上月{' '}
                            <span style={{ color: diffPct >= 0 ? '#fc1a01' : '#27b044' }}>
                              {diffPct >= 0 ? '+ ' : '- '}
                              {Math.abs(diffPct).toFixed(2)}%
                            </span>
                          </span>
                        );
                      })()}
                    </Typography>
                  </CardContent>
                </Card>
              </>
            )}

            {/* (3) 韓國線管理 → 只顯示韓國線數據 */}
            {userGroups.includes('韓國線管理') && (
              <>
                <Card elevation={3} className={styles['details-card']}>
                  <CardContent className={styles['details-cardcontent']}>
                    <LocalAtmOutlined
                      className={styles['details-icon']}
                      sx={{ backgroundColor: '#fa5a7d' }}
                    />
                    <Typography className={styles['details-number']}>
                      ₩ {currentMonthDataKRStaff?.fc_price_sum || '0'}
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold', color: '#7a7b8c' }}>
                      外幣報價總和
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#9cabe9' }}>
                      {(() => {
                        const lastSum = lastMonthDataKRStaff?.fc_price_sum || 0;
                        if (lastSum === 0) return '上月無數據，無法對比';
                        const diffPct =
                          ((currentMonthDataKRStaff.fc_price_sum - lastSum) / lastSum) * 100;
                        return (
                          <span>
                            較上月{' '}
                            <span style={{ color: diffPct >= 0 ? '#fc1a01' : '#27b044' }}>
                              {diffPct >= 0 ? '+ ' : '- '}
                              {Math.abs(diffPct).toFixed(2)}%
                            </span>
                          </span>
                        );
                      })()}
                    </Typography>
                  </CardContent>
                </Card>
                <Card elevation={3} className={styles['details-card']}>
                  <CardContent className={styles['details-cardcontent']}>
                    <PriceChange
                      className={styles['details-icon']}
                      sx={{ backgroundColor: '#749cf0' }}
                    />
                    <Typography className={styles['details-number']}>
                      ₩ {currentMonthDataKRStaff?.fc_price_estimated_sum || '0'}
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold', color: '#7a7b8c' }}>
                      預估外幣報價總和
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#9cabe9' }}>
                      {(() => {
                        const actual = currentMonthDataKRStaff.fc_price_sum || 0;
                        const estimated = currentMonthDataKRStaff.fc_price_estimated_sum || 0;
                        if (estimated === 0) return '無法計算實際報價';
                        const diffPct = ((actual - estimated) / estimated) * 100;
                        return (
                          <span>
                            實際報價{' '}
                            <span style={{ color: diffPct >= 0 ? '#fc1a01' : '#27b044' }}>
                              {diffPct >= 0 ? '+ ' : '- '}
                              {Math.abs(diffPct).toFixed(2)}%
                            </span>
                          </span>
                        );
                      })()}
                    </Typography>
                  </CardContent>
                </Card>
                <Card elevation={3} className={styles['details-card']}>
                  <CardContent className={styles['details-cardcontent']}>
                    <RequestQuote
                      className={styles['details-icon']}
                      sx={{ backgroundColor: '#96d174' }}
                    />
                    <Typography className={styles['details-number']}>
                      ₩ {currentMonthDataKRStaff?.booking_price_sum || '0'}
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold', color: '#7a7b8c' }}>
                      外站報價總和
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#9cabe9' }}>
                      {(() => {
                        const lastSum = lastMonthDataKRStaff?.booking_price_sum || 0;
                        if (lastSum === 0) return '上月無數據，無法對比';
                        const diffPct =
                          ((currentMonthDataKRStaff.booking_price_sum - lastSum) / lastSum) * 100;
                        return (
                          <span>
                            較上月{' '}
                            <span style={{ color: diffPct >= 0 ? '#fc1a01' : '#27b044' }}>
                              {diffPct >= 0 ? '+ ' : '- '}
                              {Math.abs(diffPct).toFixed(2)}%
                            </span>
                          </span>
                        );
                      })()}
                    </Typography>
                  </CardContent>
                </Card>
              </>
            )}
          </Box>
        </Box>

        {/* (管理員 / 會計人員) → AdminTab / AccountingTab 區域 */}
        {(userGroups.includes('管理員') || userGroups.includes('會計人員')) && (
          <Box className={styles['dataTable-box']}>
            <Box className={styles['dataTable-header-box']}>
              {tabValue === 0 && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '10px',
                      position: 'absolute',
                      left: 0,
                      marginLeft: '15px'
                    }}
                  >
                    <Tooltip
                      placement="top-start"
                      title={
                        <Typography sx={{ fontSize: '15px' }}>
                          外幣報價、預估外幣報價、外站報價等數據可透過「雙擊」欄位進行編輯
                        </Typography>
                      }
                    >
                      <Info className={styles['info-icon']} />
                    </Tooltip>

                    {(
                      filterSalesRep
                        ? currentMonthData.filter((order) => order.sales_rep === filterSalesRep)
                        : currentMonthData
                    ).length > 0 && (
                      <Button
                        className={styles['export-button']}
                        disableElevation
                        onClick={() =>
                          exportDashBoardDataToExcel(
                            filterSalesRep
                              ? currentMonthData.filter((order) => order.sales_rep === filterSalesRep)
                              : currentMonthData,
                            filterSalesRep
                              ? {
                                  year,
                                  month,
                                  salesRep:
                                    salesRepList.find((user) => user.id === filterSalesRep)
                                      ?.profile?.nickname ||
                                    salesRepList.find((user) => user.id === filterSalesRep)
                                      ?.username
                                }
                              : { year, month }
                          )
                        }
                      >
                        <SaveAlt sx={{ width: '16px', height: '16px' }} />
                        導出{year}年{month}月數據
                        {filterSalesRep &&
                          ` - ${
                            salesRepList.find((user) => user.id === filterSalesRep)?.profile
                              ?.nickname ||
                            salesRepList.find((user) => user.id === filterSalesRep)?.username
                          }`}
                      </Button>
                    )}
                  </Box>

                  <Box className={styles['filter-box']}>
                    {/* 業務篩選 */}
                    <Autocomplete
                      options={salesRepList.filter(
                        (user) =>
                          user.profile.tags === '台灣業務' ||
                          user.profile.tags === '台灣管理' ||
                          user.profile.tags === '管理人員'
                      )}
                      getOptionLabel={(option) =>
                        option?.profile?.nickname || option?.username
                      }
                      value={
                        filterSalesRep
                          ? salesRepList.find((user) => user.id === filterSalesRep)
                          : null
                      }
                      sx={{
                        minWidth: '180px',
                        '& .MuiInput-root.MuiInputBase-sizeSmall': {
                          '& .MuiInput-input': { paddingTop: '4px', paddingBottom: '1px' }
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          variant="standard"
                          placeholder="業務篩選"
                          InputProps={{
                            ...params.InputProps,
                            style: { color: 'white', fontWeight: 'bold' }
                          }}
                        />
                      )}
                      onChange={(event, newValue) => setFilterSalesRep(newValue?.id)}
                    />

                    {/* 創建日期篩選 */}
                    <TextField
                      value={filterCreatedDate}
                      type="date"
                      size="small"
                      variant="standard"
                      InputProps={{ style: { color: 'white', fontWeight: 'bold' } }}
                      sx={{
                        minWidth: '180px',
                        '& .MuiInput-input': { paddingTop: '5px', paddingBottom: '1px' }
                      }}
                      onChange={(event) => setFilterCreatedDate(event.target.value)}
                    />
                  </Box>
                </>
              )}

              {/* Tab 切換按鈕 */}
              <Box className={styles['options-box']}>
                <Button
                  className={styles['options-button-1']}
                  disableRipple
                  sx={
                    tabValue === 0
                      ? { backgroundColor: '#4b6ab9', ':hover': { backgroundColor: '#4b6ab9' } }
                      : {}
                  }
                  onClick={() => handleTabChange(0)}
                >
                  收益
                </Button>
                <Button
                  className={styles['options-button-2']}
                  disableRipple
                  sx={
                    tabValue === 1
                      ? { backgroundColor: '#4b6ab9', ':hover': { backgroundColor: '#4b6ab9' } }
                      : {}
                  }
                  onClick={() => handleTabChange(1)}
                >
                  款項
                </Button>
              </Box>
            </Box>

            {renderTabContent()}
          </Box>
        )}
      </Box>
    </Container>
  );
}

export default DashBoard;
